import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import SortProductsModal from './SortProductsModal';
import ProductAttributesModalV2 from '../ProductAttributesModalV2';
import BulkDiscountsModal from './BulkDiscountsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ProductManagementSection({ printSizes, onUpdateProducts }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingProduct, setAddingProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editValues, setEditValues] = useState({}); // Store edit values separately
  const [newProduct, setNewProduct] = useState({ 
    sku: '', 
    size: '', 
    name: '', 
    price: '', 
    crop_ratio_x: '',
    crop_ratio_y: '',
    image_url: '', 
    categories: [],
    featured: false
  });
  const [categories, setCategories] = useState([]);
  const [testMode, setTestMode] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editUploadingImage, setEditUploadingImage] = useState(false);
  const [sortModalOpen, setSortModalOpen] = useState(false);
  const [bulkDiscountsModalOpen, setBulkDiscountsModalOpen] = useState(false);
  const [showAttributesModal, setShowAttributesModal] = useState(false);
  const [attributesModalTab, setAttributesModalTab] = useState('assign');

  // Helper function to fix image URLs
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    // If it's already a full URL with the current BACKEND_URL, use it
    if (imageUrl.startsWith(BACKEND_URL)) {
      return imageUrl;
    }
    
    // If it's a full URL with http/https but wrong domain, extract filename and rebuild
    if (imageUrl.startsWith('http')) {
      const filename = imageUrl.split('/').pop();
      return `${BACKEND_URL}/api/images/view/${filename}`;
    }
    
    // If it's a relative path, construct the full URL
    // Handle both /api/images/view/ and /images/view/ formats
    if (imageUrl.includes('/images/view/')) {
      const filename = imageUrl.split('/').pop();
      return `${BACKEND_URL}/api/images/view/${filename}`;
    }
    
    // Default: assume it's just a filename
    return `${BACKEND_URL}/api/images/view/${imageUrl}`;
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/admin/categories');
      const cats = response.data.categories || [];
      // Sort by order_index to show reordered categories correctly
      cats.sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
      setCategories(cats);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/admin/products');
      if (response.data && response.data.products) {
        // Convert to array format [sku, product] for compatibility
        const productsArray = response.data.products.map(p => [p.sku, p]);
        setProducts(productsArray);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };


  const handleImageUpload = async (file, isEdit = false) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    if (isEdit) {
      setEditUploadingImage(true);
    } else {
      setUploadingImage(true);
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post('/admin/products/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data && response.data.image_url) {
        // Backend returns /images/view/{filename}, we need to add /api prefix
        const fullImageUrl = `${BACKEND_URL}/api${response.data.image_url}`;
        
        if (isEdit) {
          setEditValues(prev => ({ ...prev, image_url: fullImageUrl }));
        } else {
          setNewProduct(prev => ({ ...prev, image_url: fullImageUrl }));
        }
        
        toast.success('✅ Image uploaded successfully!');
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to upload image');
    } finally {
      if (isEdit) {
        setEditUploadingImage(false);
      } else {
        setUploadingImage(false);
      }
    }
  };

  const startEditing = (sku, product) => {
    setEditingProduct(sku);
    setEditValues({
      sku: sku,
      size: product.size || '',
      name: product.name,
      priceDisplay: (product.price / 100).toFixed(2),
      crop_ratio_x: product.crop_ratio_x || '',
      crop_ratio_y: product.crop_ratio_y || '',
      image_url: product.image_url || '',
      categories: product.categories || [],
      featured: product.featured || false
    });
  };

  const handleUpdateProduct = async (sku) => {
    setLoading(true);
    try {
      const dollarAmount = parseFloat(editValues.priceDisplay) || 0;
      const centsAmount = Math.round(dollarAmount * 100);
      
      // Parse crop ratios as floats to support decimals
      const ratioX = editValues.crop_ratio_x ? parseFloat(editValues.crop_ratio_x) : null;
      const ratioY = editValues.crop_ratio_y ? parseFloat(editValues.crop_ratio_y) : null;
      
      await axios.put(`/admin/products/${sku}`, {
        sku: editValues.sku,
        size: editValues.size,
        name: editValues.name,
        price: centsAmount,
        crop_ratio_x: ratioX,
        crop_ratio_y: ratioY,
        image_url: editValues.image_url,
        categories: editValues.categories,
        featured: editValues.featured
      });
      toast.success('✅ Product updated successfully!');
      setEditingProduct(null);
      setEditValues({});
      await fetchProducts();
      if (onUpdateProducts) await onUpdateProducts();
    } catch (error) {
      console.error('Failed to update product:', error);
      const message = error.response?.data?.detail || 'Failed to update product';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };


  const handleMoveProduct = async (currentIndex, direction) => {
    const newIndex = currentIndex + direction;
    if (newIndex < 0 || newIndex >= products.length) return;
    
    const items = Array.from(products);
    const [movedItem] = items.splice(currentIndex, 1);
    items.splice(newIndex, 0, movedItem);
    
    // Update local state immediately for instant feedback
    setProducts(items);
    
    // Send to backend
    try {
      const productOrders = items.map((item, index) => ({
        sku: item[0], // product SKU
        sort_order: index
      }));
      await axios.put('/admin/products/reorder', productOrders);
      toast.success('✅ Product order updated');
      // Don't call onUpdateProducts - it's not needed and causes issues
      // The local state already has the correct order
    } catch (error) {
      console.error('Failed to reorder products:', error);
      toast.error('Failed to reorder products');
      // Only refetch on error to revert
      await fetchProducts();
    }
  };


  const handleDeleteProduct = async (sku) => {
    if (!window.confirm(`Are you sure you want to delete product "${sku}"?`)) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`/admin/products/${sku}`);
      toast.success('✅ Product deleted successfully!');
      await fetchProducts();
      if (onUpdateProducts) await onUpdateProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async () => {
    if (!newProduct.sku || !newProduct.name || !newProduct.price) {
      toast.error('Please fill in SKU, name, and price');
      return;
    }

    setAddingProduct(true);
    try {
      const dollarAmount = parseFloat(newProduct.price) || 0;
      const centsAmount = Math.round(dollarAmount * 100);
      
      // Parse crop ratios as floats to support decimals
      const ratioX = newProduct.crop_ratio_x ? parseFloat(newProduct.crop_ratio_x) : null;
      const ratioY = newProduct.crop_ratio_y ? parseFloat(newProduct.crop_ratio_y) : null;
      
      await axios.post('/admin/products', {
        sku: newProduct.sku,
        size: newProduct.size,
        name: newProduct.name,
        price: centsAmount,
        crop_ratio_x: ratioX,
        crop_ratio_y: ratioY,
        image_url: newProduct.image_url,
        categories: newProduct.categories,
        featured: newProduct.featured,
        active: true
      });
      toast.success('✅ Product added successfully!');
      setNewProduct({ sku: '', size: '', name: '', price: '', crop_ratio_x: '', crop_ratio_y: '', image_url: '', categories: [], featured: false });
      // Small delay to ensure backend has committed the data
      await new Promise(resolve => setTimeout(resolve, 100));
      await fetchProducts();
      if (onUpdateProducts) await onUpdateProducts();
    } catch (error) {
      console.error('Failed to add product:', error);
      const message = error.response?.data?.detail || 'Failed to add product';
      toast.error(message);
    } finally {
      setAddingProduct(false);
    }
  };

  const handleReorderProducts = async (productOrders) => {
    setLoading(true);
    try {
      await axios.put('/admin/products/reorder', { products: productOrders });
      toast.success('✅ Product order saved!');
      // Small delay to ensure backend has committed the data
      await new Promise(resolve => setTimeout(resolve, 200));
      await fetchProducts();
      if (onUpdateProducts) await onUpdateProducts();
      // Close the modal after successful save
      setSortModalOpen(false);
    } catch (error) {
      console.error('Failed to save order:', error);
      toast.error('Failed to save order');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAttributes = async (sku, attributes) => {
    setLoading(true);
    try {
      await axios.put(`/admin/products/${sku}`, attributes);
      toast.success('✅ Product attributes updated!');
      await fetchProducts();
      if (onUpdateProducts) await onUpdateProducts();
    } catch (error) {
      console.error('Failed to update attributes:', error);
      toast.error('Failed to update attributes');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSaveAttributes = async (updates) => {
    setLoading(true);
    try {
      // Update each product with its attributes
      await Promise.all(
        updates.map(({ sku, paper_types, border_options }) =>
          axios.put(`/admin/products/${sku}`, { paper_types, border_options })
        )
      );
      await fetchProducts();
      if (onUpdateProducts) await onUpdateProducts();
    } catch (error) {
      console.error('Failed to update attributes:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Product Management</h2>
        <p className="text-gray-600">Manage print sizes, prices, and product options</p>
        <div className="flex justify-center gap-3 mt-4">
          <Button 
            onClick={() => {
              console.log('Sort Products button clicked, opening modal');
              setSortModalOpen(true);
            }}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="sort-products-button"
          >
            🔄 Sort Products
          </Button>
          <Button 
            onClick={() => setBulkDiscountsModalOpen(true)}
            className="bg-green-600 hover:bg-green-700"
          >
            💰 Bulk Discounts
          </Button>
          <Button 
            onClick={() => {
              setAttributesModalTab('assign');
              setShowAttributesModal(true);
            }}
            className="bg-custom-orange hover:bg-custom-orange"
          >
            ⚙️ Product Attributes
          </Button>
        </div>
      </div>

      {/* Add New Product */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Product</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="SKU (e.g., PRINT-12X16) *Required"
            value={newProduct.sku}
            onChange={(e) => setNewProduct(prev => ({ ...prev, sku: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            data-testid="new-product-sku"
            required
          />
          <input
            type="text"
            placeholder="Size (e.g., 12x16) Optional"
            value={newProduct.size}
            onChange={(e) => setNewProduct(prev => ({ ...prev, size: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            data-testid="new-product-size"
          />
          <input
            type="text"
            placeholder='Display name (e.g., 12" x 16") *Required'
            value={newProduct.name}
            onChange={(e) => setNewProduct(prev => ({ ...prev, name: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            data-testid="new-product-name"
            required
          />
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600">$</span>
            <input
              type="text"
              placeholder="Price (e.g., 12.99) *Required"
              pattern="[0-9]*\.?[0-9]*"
              value={newProduct.price}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.]/g, '');
                setNewProduct(prev => ({ ...prev, price: value }));
              }}
              className="w-full pl-7 pr-12 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              data-testid="new-product-price"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">AUD</span>
          </div>
          <div className="flex gap-2">
            <input
              type="number"
              step="0.1"
              placeholder="Ratio X (e.g., 3, 3.5)"
              value={newProduct.crop_ratio_x}
              onChange={(e) => setNewProduct(prev => ({ ...prev, crop_ratio_x: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              data-testid="new-product-ratio-x"
              min="0.1"
              max="64"
            />
            <span className="flex items-center text-gray-600 font-bold">:</span>
            <input
              type="number"
              step="0.1"
              placeholder="Ratio Y (e.g., 2, 3.5)"
              value={newProduct.crop_ratio_y}
              onChange={(e) => setNewProduct(prev => ({ ...prev, crop_ratio_y: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              data-testid="new-product-ratio-y"
              min="0.1"
              max="64"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Product Image (optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) handleImageUpload(file, false);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              data-testid="new-product-image"
              disabled={uploadingImage}
            />
            {uploadingImage && <p className="text-sm text-blue-600">Uploading image...</p>}
            {newProduct.image_url && (
              <div className="mt-2">
                <img 
                  src={getImageUrl(newProduct.image_url)} 
                  alt="Preview" 
                  className="w-20 h-20 object-cover rounded border"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
          <select
            multiple
            value={newProduct.categories}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, option => option.value);
              setNewProduct(prev => ({ ...prev, categories: selected }));
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            data-testid="new-product-categories"
          >
            <option value="" disabled>Select Categories (optional)</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="new-product-featured"
              checked={newProduct.featured}
              onChange={(e) => setNewProduct(prev => ({ ...prev, featured: e.target.checked }))}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="new-product-featured" className="text-sm font-medium text-gray-700">
              Featured
            </label>
          </div>
          <Button 
            onClick={handleAddProduct}
            disabled={addingProduct}
            className="bg-custom-orange hover:bg-custom-orange"
            data-testid="add-product-button"
          >
            {addingProduct ? 'Adding...' : 'Add Product'}
          </Button>
        </div>
      </Card>

      {/* Existing Products */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Products</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-2">Image</th>
                <th className="text-left py-3 px-2">SKU</th>
                <th className="text-left py-3 px-2">Size</th>
                <th className="text-left py-3 px-2">Display Name</th>
                <th className="text-left py-3 px-2">Crop Ratio</th>
                <th className="text-left py-3 px-2">Price</th>
                <th className="text-left py-3 px-2">Categories</th>
                <th className="text-left py-3 px-2">Featured</th>
                <th className="text-left py-3 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(([sku, product], index) => (
                <tr key={sku} className="border-b border-gray-100 hover:bg-gray-50">
                  {editingProduct === sku ? (
                    <>
                      <td className="py-3 px-2">
                        <div className="space-y-1">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) handleImageUpload(file, true);
                            }}
                            className="w-full px-1 py-1 border border-gray-300 rounded text-xs"
                            disabled={editUploadingImage}
                          />
                          {editUploadingImage && <p className="text-xs text-blue-600">Uploading...</p>}
                          {editValues.image_url && (
                            <img 
                              src={getImageUrl(editValues.image_url)} 
                              alt="Preview" 
                              className="w-12 h-12 object-cover rounded border"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <input
                          type="text"
                          value={editValues.sku || ''}
                          onChange={(e) => setEditValues(prev => ({ ...prev, sku: e.target.value }))}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="e.g., PRINT-8X10"
                          data-testid={`edit-sku-${sku}`}
                        />
                      </td>
                      <td className="py-3 px-2">
                        <input
                          type="text"
                          value={editValues.size || ''}
                          onChange={(e) => setEditValues(prev => ({ ...prev, size: e.target.value }))}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="e.g., 8x10"
                          data-testid={`edit-size-${sku}`}
                        />
                      </td>
                      <td className="py-3 px-2">
                        <input
                          type="text"
                          value={editValues.name || ''}
                          onChange={(e) => setEditValues(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder='e.g., 8" x 10"'
                          data-testid={`edit-name-${sku}`}
                        />
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            step="0.1"
                            value={editValues.crop_ratio_x || ''}
                            onChange={(e) => setEditValues(prev => ({ ...prev, crop_ratio_x: e.target.value }))}
                            className="w-16 px-1 py-1 border border-gray-300 rounded text-sm"
                            placeholder="X"
                            min="0.1"
                            max="64"
                          />
                          <span className="text-gray-600">:</span>
                          <input
                            type="number"
                            step="0.1"
                            value={editValues.crop_ratio_y || ''}
                            onChange={(e) => setEditValues(prev => ({ ...prev, crop_ratio_y: e.target.value }))}
                            className="w-16 px-1 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Y"
                            min="0.1"
                            max="64"
                          />
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-600">$</span>
                          <input
                            type="text"
                            value={editValues.priceDisplay || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9.]/g, '');
                              setEditValues(prev => ({ ...prev, priceDisplay: value }));
                            }}
                            placeholder="0.00"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            data-testid={`edit-price-${sku}`}
                          />
                          <span className="text-xs text-gray-500">AUD</span>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <select
                          multiple
                          value={editValues.categories || []}
                          onChange={(e) => {
                            const selected = Array.from(e.target.selectedOptions, option => option.value);
                            setEditValues(prev => ({ ...prev, categories: selected }));
                          }}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          size="3"
                        >
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            checked={editValues.featured || false}
                            onChange={(e) => setEditValues(prev => ({ ...prev, featured: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                        </div>
                      </td>
                      <td className="py-3 px-2 space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => {
                            setEditingProduct(null);
                            setEditValues({});
                          }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-custom-orange hover:bg-custom-orange"
                          onClick={() => handleUpdateProduct(sku)}
                          disabled={loading}
                        >
                          {loading ? 'Saving...' : 'Save'}
                        </Button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-3 px-2">
                        {product.image_url ? (
                          <img 
                            src={getImageUrl(product.image_url)} 
                            alt={product.name} 
                            className="w-12 h-12 object-cover rounded"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIGZpbGw9IiNFNUU3RUIiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzlDQTNBRiIgZm9udC1zaXplPSIxMiI+Tm8gSW1nPC90ZXh0Pjwvc3ZnPg==';
                            }}
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-400">
                            No Image
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-2 font-medium">{sku}</td>
                      <td className="py-3 px-2 text-gray-600">{product.size || '—'}</td>
                      <td className="py-3 px-2">{product.name}</td>
                      <td className="py-3 px-2 text-gray-600">
                        {product.crop_ratio_x && product.crop_ratio_y 
                          ? `${product.crop_ratio_x}:${product.crop_ratio_y}` 
                          : '—'}
                      </td>
                      <td className="py-3 px-2">${(product.price / 100).toFixed(2)}</td>
                      <td className="py-3 px-2">
                        {product.categories && product.categories.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {product.categories.map(catId => {
                              const cat = categories.find(c => c.id === catId);
                              return cat ? (
                                <Badge key={catId} variant="secondary" className="text-xs">
                                  {cat.name}
                                </Badge>
                              ) : null;
                            })}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">
                        {product.featured ? (
                          <Badge variant="default" className="bg-yellow-500 text-white">⭐ Featured</Badge>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-2 space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => startEditing(sku, product)}
                          data-testid={`edit-${sku}`}
                          disabled={loading}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleDeleteProduct(sku)}
                          data-testid={`delete-${sku}`}
                          disabled={loading}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Delete
                        </Button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Sort Products Modal */}
      <SortProductsModal
        products={products}
        onReorder={handleReorderProducts}
        isOpen={sortModalOpen}
        onClose={() => setSortModalOpen(false)}
      />

      {/* Product Attributes Modal */}
      <ProductAttributesModalV2
        products={products}
        onSaveBulk={handleBulkSaveAttributes}
        isOpen={showAttributesModal}
        onClose={() => setShowAttributesModal(false)}
        initialTab={attributesModalTab}
      />

      {/* Bulk Discounts Modal */}
      <BulkDiscountsModal
        isOpen={bulkDiscountsModalOpen}
        onClose={() => setBulkDiscountsModalOpen(false)}
        onUpdate={fetchProducts}
      />
    </div>
  );
}


export default ProductManagementSection;
